import React, { useState } from 'react';
import { Plus, Trash2, Copy, Sparkles, Briefcase, ChevronDown, ChevronUp } from 'lucide-react';
import { PageSection } from '../../../../types';
import { AVAILABLE_ICONS } from '../../../../utils/editorHelpers';

interface ServicesEditorProps {
  section: PageSection;
  onUpdateContent: (updates: any) => void;
}

export const ServicesEditor: React.FC<ServicesEditorProps> = ({ section, onUpdateContent }) => {
  const content = section.content || {};
  const items: any[] = content.items || [
    { id: '1', title: 'Stratejik Danışmanlık', description: 'Kurumunuza özel çözümler ve yol haritası.', icon: 'Compass' },
    { id: '2', title: 'Operasyonel Yönetim', description: 'Verimli ve sürdürülebilir süreç optimizasyonu.', icon: 'TrendingUp' },
    { id: '3', title: 'Teknoloji & AI Altyapı', description: 'Modern dijital çözümler ve entegrasyon.', icon: 'Cpu' },
  ];

  const [activeItemId, setActiveItemId] = useState<string | null>(items[0]?.id || null);

  const handleUpdateItem = (index: number, updates: any) => {
    const next = [...items];
    next[index] = { ...next[index], ...updates };
    onUpdateContent({ items: next });
  };

  const handleAddItem = () => {
    const newItem = {
      id: `srv_${Date.now().toString(36)}`,
      title: `Yeni Hizmet ${items.length + 1}`,
      description: 'Hizmetin sağladığı avantajlar ve kapsam.',
      icon: 'Star',
    };
    const next = [...items, newItem];
    onUpdateContent({ items: next });
    setActiveItemId(newItem.id);
  };

  const handleDuplicateItem = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const source = items[index];
    const duplicated = {
      ...JSON.parse(JSON.stringify(source)),
      id: `srv_${Date.now().toString(36)}`,
      title: `${source.title} (Kopya)`,
    };
    const next = [...items];
    next.splice(index + 1, 0, duplicated);
    onUpdateContent({ items: next });
    setActiveItemId(duplicated.id);
  };

  const handleDeleteItem = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = items.filter((_, i) => i !== index);
    onUpdateContent({ items: next });
    if (activeItemId === items[index]?.id) {
      setActiveItemId(next[0]?.id || null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Section Header & Layout */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Bölüm Başlıkları & Düzen</h4>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Sütun Sayısı</label>
          <div className="grid grid-cols-3 gap-2">
            {[2, 3, 4].map((col) => {
              const currentCols = Number(content.columns) || 3;
              const isSelected = currentCols === col;
              return (
                <button
                  key={col}
                  type="button"
                  onClick={() => onUpdateContent({ columns: col })}
                  className={`py-1.5 px-3 rounded-xl text-xs font-bold transition border ${
                    isSelected
                      ? 'bg-blue-600 text-white border-blue-500 shadow-sm'
                      : 'bg-slate-900 text-slate-300 border-slate-700 hover:border-slate-600'
                  }`}
                >
                  {col} Sütun
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Üst Rozet (Badge)</label>
          <input
            type="text"
            value={content.badge ?? 'HİZMETLERİMİZ'}
            onChange={(e) => onUpdateContent({ badge: e.target.value })}
            placeholder="Örn: UZMANLIK ALANLARI"
            className="w-full bg-slate-900 border border-slate-700 text-xs font-semibold text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ana Başlık</label>
          <input
            type="text"
            value={content.title ?? 'İşletmenizi Zirveye Taşıyan Çözümler'}
            onChange={(e) => onUpdateContent({ title: e.target.value })}
            placeholder="Başlık..."
            className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Açıklama Metni</label>
          <textarea
            value={content.description ?? ''}
            onChange={(e) => onUpdateContent({ description: e.target.value })}
            rows={2}
            placeholder="Kısa açıklama..."
            className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 p-2.5 rounded-xl outline-none focus:border-blue-500 resize-none"
          />
        </div>
      </div>

      {/* Services List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-blue-400" />
            <h4 className="text-xs font-extrabold text-slate-200">Hizmet Kartları ({items.length})</h4>
          </div>
          <button
            type="button"
            onClick={handleAddItem}
            className="px-2.5 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-bold flex items-center gap-1.5 shadow-sm transition"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Hizmet Ekle</span>
          </button>
        </div>

        <div className="space-y-2.5">
          {items.map((item, idx) => {
            const isExpanded = activeItemId === item.id;
            const IconComp = AVAILABLE_ICONS[item.icon]?.icon || Briefcase;

            return (
              <div
                key={item.id || idx}
                className={`rounded-2xl border transition-all ${
                  isExpanded
                    ? 'bg-slate-800/90 border-blue-500/50 shadow-md ring-1 ring-blue-500/20'
                    : 'bg-slate-800/40 border-slate-700/60 hover:border-slate-600'
                }`}
              >
                {/* Header */}
                <div
                  onClick={() => setActiveItemId(isExpanded ? null : item.id)}
                  className="p-3 flex items-center justify-between cursor-pointer select-none"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-7 h-7 rounded-lg bg-slate-900 border border-slate-700 flex items-center justify-center text-blue-400">
                      <IconComp className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-slate-200 truncate">{item.title}</div>
                      <div className="text-[10px] text-slate-400 truncate">{item.description || 'Açıklama yok'}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={(e) => handleDuplicateItem(idx, e)}
                      title="Kopyala"
                      className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDeleteItem(idx, e)}
                      title="Sil"
                      className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <div className="p-1 text-slate-400">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </div>
                </div>

                {/* Expanded Fields */}
                {isExpanded && (
                  <div className="p-4 pt-2 border-t border-slate-700/50 space-y-3.5">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Hizmet Adı</label>
                      <input
                        type="text"
                        value={item.title || ''}
                        onChange={(e) => handleUpdateItem(idx, { title: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Fiyat / Rozet (Opsiyonel)</label>
                        <input
                          type="text"
                          value={item.price || ''}
                          onChange={(e) => handleUpdateItem(idx, { price: e.target.value })}
                          placeholder="Örn: 2.500 ₺'den başlayan"
                          className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Görsel URL (Opsiyonel)</label>
                        <input
                          type="text"
                          value={item.coverImage || ''}
                          onChange={(e) => handleUpdateItem(idx, { coverImage: e.target.value })}
                          placeholder="https://..."
                          className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Açıklama</label>
                      <textarea
                        value={item.description || ''}
                        onChange={(e) => handleUpdateItem(idx, { description: e.target.value })}
                        rows={2}
                        className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 p-2 rounded-xl outline-none focus:border-blue-500 resize-none"
                      />
                    </div>

                    {/* Icon Selection Grid */}
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">İkon Seçin</label>
                      <div className="grid grid-cols-4 gap-2">
                        {Object.entries(AVAILABLE_ICONS).map(([key, data]) => {
                          const Icon = data.icon;
                          const isSelected = item.icon === key;
                          return (
                            <button
                              key={key}
                              type="button"
                              title={data.label}
                              onClick={() => handleUpdateItem(idx, { icon: key })}
                              className={`p-2 rounded-xl border flex flex-col items-center justify-center gap-1 transition ${
                                isSelected
                                  ? 'bg-blue-600 text-white border-blue-500 shadow-sm'
                                  : 'bg-slate-900 text-slate-300 border-slate-700 hover:border-slate-500'
                              }`}
                            >
                              <Icon className="w-4 h-4" />
                              <span className="text-[9px] truncate max-w-full font-medium">{key}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
